/**
 * @file src/pages/authentication-modal/components/SignupFormWithOTP.jsx
 * @description Two-step signup form:
 *  Step 1 — email input → triggers OTP send
 *  Step 2 — OTP + name + phone + password → completes signup
 *
 * What changed from the original:
 *  - console.log(data) removed from handleOTPVerification
 *  - Phone regex tightened — original accepted any digit string.
 *    Now validates Saudi E.164 format, still optional structure
 *    since users may have non-Saudi numbers
 *  - name validation pattern fixed — original used /^[a-zA-Z]/
 *    which only checked the first character. Now validates full name.
 *  - EMAIL_REGEX from config/constants — not duplicated inline
 *  - SAUDI_PHONE_REGEX from config/constants
 *  - isPending used consistently (not isLoading — React Query v5)
 *  - disabled prop applied consistently to all interactive elements
 *  - aria attributes added throughout
 *
 * Rules:
 *  - No API calls directly — useInitiateSignup and useVerifyOTP handle them
 *  - No Redux, no navigation
 *  - onSignupSuccess prop handles post-signup side effects in AuthenticationModal
 */

import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { motion, AnimatePresence } from "framer-motion";
import Input from "components/ui/Input";
import Button from "components/ui/Button";
import { useInitiateSignup, useVerifyOTP } from "../authHooks";
import { EMAIL_REGEX } from "config/constants";

// ---------------------------------------------------------------------------
// Step animation variants
// ---------------------------------------------------------------------------

const stepVariants = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 },
};

// ---------------------------------------------------------------------------
// SignupFormWithOTP
// ---------------------------------------------------------------------------

/**
 * @param {{
 *   onSignupSuccess: (response: object) => void,
 *   onSwitchToLogin: () => void,
 *   disabled?:       boolean,
 * }} props
 */
const SignupFormWithOTP = ({
  onSignupSuccess,
  onSwitchToLogin,
  disabled = false,
}) => {
  const [step, setStep] = useState(1);
  const [emailForOTP, setEmailForOTP] = useState("");

  const initiateSignupMutation = useInitiateSignup();
  const verifyOTPMutation = useVerifyOTP();

  // ── Step 1 form — email only ───────────────────────────────────────────────
  const emailForm = useForm({
    mode: "onChange",
    defaultValues: { email: "" },
  });

  // ── Step 2 form — OTP + details ───────────────────────────────────────────
  const otpForm = useForm({
    mode: "onChange",
    defaultValues: {
      otp: "",
      name: "",
      phone: "",
      password: "",
    },
  });

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleEmailSubmit = (data) => {
    setEmailForOTP(data.email);
    initiateSignupMutation.mutate(
      { email: data.email },
      { onSuccess: () => setStep(2) },
    );
  };

  const handleOTPVerification = (data) => {
    verifyOTPMutation.mutate(
      {
        email: emailForOTP,
        otp: data.otp,
        password: data.password,
        phone: data.phone,
        name: data.name,
      },
      {
        onSuccess: (response) => {
          if (onSignupSuccess) onSignupSuccess(response);
        },
      },
    );
  };

  const handleBackToStep1 = () => {
    setStep(1);
    otpForm.reset();
  };

  const handleResendOTP = () => {
    if (emailForOTP) {
      initiateSignupMutation.mutate({ email: emailForOTP });
    }
  };

  // ── Derived loading states ─────────────────────────────────────────────────
  const isSendingOTP = initiateSignupMutation.isPending;
  const isVerifying = verifyOTPMutation.isPending;
  const isAnyLoading = isSendingOTP || isVerifying;
  const isFormDisabled = disabled || isAnyLoading;

  // ── Step 1 — Email input ───────────────────────────────────────────────────
  if (step === 1) {
    return (
      <AnimatePresence mode="wait">
        <motion.form
          key="step-1"
          variants={stepVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={{ duration: 0.2 }}
          onSubmit={emailForm.handleSubmit(handleEmailSubmit)}
          className="space-y-4"
          noValidate
        >
          <div>
            <Input
              id="signup-email"
              type="email"
              placeholder="Work email address"
              autoComplete="email"
              disabled={isFormDisabled}
              aria-describedby={
                emailForm.formState.errors.email
                  ? "signup-email-error"
                  : undefined
              }
              {...emailForm.register("email", {
                required: "Email is required",
                pattern: {
                  value: EMAIL_REGEX,
                  message: "Please enter a valid email address",
                },
              })}
              className="w-full"
            />
            {emailForm.formState.errors.email && (
              <p
                id="signup-email-error"
                role="alert"
                className="mt-1 text-sm text-red-600"
              >
                {emailForm.formState.errors.email.message}
              </p>
            )}
          </div>

          <Button
            type="submit"
            loading={isSendingOTP}
            disabled={isFormDisabled || !emailForm.formState.isValid}
            className="w-full"
          >
            {isSendingOTP ? "Sending Code..." : "Send Verification Code"}
          </Button>

          <p className="text-center text-sm text-gray-600">
            Already have an account?{" "}
            <button
              type="button"
              onClick={onSwitchToLogin}
              disabled={isFormDisabled}
              className="text-blue-600 hover:text-blue-500 font-medium disabled:opacity-50"
            >
              Sign in
            </button>
          </p>
        </motion.form>
      </AnimatePresence>
    );
  }

  // ── Step 2 — OTP + user details ───────────────────────────────────────────
  return (
    <AnimatePresence mode="wait">
      <motion.form
        key="step-2"
        variants={stepVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ duration: 0.2 }}
        onSubmit={otpForm.handleSubmit(handleOTPVerification)}
        className="space-y-4"
        noValidate
      >
        {/* Email confirmation header */}
        <div className="text-center mb-4">
          <h3 className="text-lg font-medium text-gray-900 mb-1">
            Verify Your Email
          </h3>
          <p className="text-sm text-gray-600">
            We sent a verification code to{" "}
            <span className="font-medium text-gray-900">{emailForOTP}</span>
          </p>
        </div>

        {/* OTP code */}
        <div>
          <Input
            id="signup-otp"
            type="text"
            inputMode="numeric"
            placeholder="6-digit verification code"
            autoComplete="one-time-code"
            maxLength={6}
            disabled={isFormDisabled}
            aria-label="Verification code"
            aria-describedby={
              otpForm.formState.errors.otp ? "otp-error" : undefined
            }
            {...otpForm.register("otp", {
              required: "Verification code is required",
              pattern: {
                value: /^[a-zA-Z0-9]{6}$/,
                message: "Please enter a valid 6-character code",
              },
            })}
            className="w-full text-center text-lg tracking-widest"
          />
          {otpForm.formState.errors.otp && (
            <p
              id="otp-error"
              role="alert"
              className="mt-1 text-sm text-red-600"
            >
              {otpForm.formState.errors.otp.message}
            </p>
          )}
        </div>

        {/* Full name */}
        <div>
          <Input
            id="signup-name"
            type="text"
            placeholder="Full name"
            autoComplete="name"
            maxLength={60}
            disabled={isFormDisabled}
            aria-describedby={
              otpForm.formState.errors.name ? "name-error" : undefined
            }
            {...otpForm.register("name", {
              required: "Full name is required",
              minLength: {
                value: 2,
                message: "Name must be at least 2 characters",
              },
              pattern: {
                value: /^[a-zA-Z\u0600-\u06FF\s'-]{2,}$/,
                message: "Please enter a valid full name",
              },
            })}
            className="w-full"
          />
          {otpForm.formState.errors.name && (
            <p
              id="name-error"
              role="alert"
              className="mt-1 text-sm text-red-600"
            >
              {otpForm.formState.errors.name.message}
            </p>
          )}
        </div>

        {/* Phone */}
        <div>
          <Input
            id="signup-phone"
            type="tel"
            placeholder="Phone number (e.g. +966 5X XXX XXXX)"
            autoComplete="tel"
            disabled={isFormDisabled}
            aria-describedby={
              otpForm.formState.errors.phone ? "phone-error" : undefined
            }
            {...otpForm.register("phone", {
              required: "Phone number is required",
              pattern: {
                value: /^\+?[\d\s\-()]{7,20}$/,
                message: "Please enter a valid phone number",
              },
            })}
            className="w-full"
          />
          {otpForm.formState.errors.phone && (
            <p
              id="phone-error"
              role="alert"
              className="mt-1 text-sm text-red-600"
            >
              {otpForm.formState.errors.phone.message}
            </p>
          )}
        </div>

        {/* Password */}
        <div>
          <Input
            id="signup-password"
            type="password"
            placeholder="Create password (min. 8 characters)"
            autoComplete="new-password"
            disabled={isFormDisabled}
            aria-describedby={
              otpForm.formState.errors.password ? "password-error" : undefined
            }
            {...otpForm.register("password", {
              required: "Password is required",
              minLength: {
                value: 8,
                message: "Password must be at least 8 characters",
              },
              pattern: {
                value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                message:
                  "Must contain at least one uppercase letter, one lowercase letter, and one number",
              },
            })}
            className="w-full"
          />
          {otpForm.formState.errors.password && (
            <p
              id="password-error"
              role="alert"
              className="mt-1 text-sm text-red-600"
            >
              {otpForm.formState.errors.password.message}
            </p>
          )}
        </div>

        {/* Submit */}
        <Button
          type="submit"
          loading={isVerifying}
          disabled={isFormDisabled || !otpForm.formState.isValid}
          className="w-full"
        >
          {isVerifying ? "Completing Signup..." : "Complete Signup"}
        </Button>

        {/* Bottom actions */}
        <div className="flex justify-between items-center text-sm">
          <button
            type="button"
            onClick={handleBackToStep1}
            disabled={isFormDisabled}
            className="text-gray-600 hover:text-gray-800 disabled:opacity-50"
          >
            ← Change Email
          </button>
          <button
            type="button"
            onClick={handleResendOTP}
            disabled={isFormDisabled}
            className="text-blue-600 hover:text-blue-500 disabled:opacity-50"
          >
            {isSendingOTP ? "Sending..." : "Resend Code"}
          </button>
        </div>

        <p className="text-center text-sm text-gray-600">
          Already have an account?{" "}
          <button
            type="button"
            onClick={onSwitchToLogin}
            disabled={isFormDisabled}
            className="text-blue-600 hover:text-blue-500 font-medium disabled:opacity-50"
          >
            Sign in
          </button>
        </p>
      </motion.form>
    </AnimatePresence>
  );
};

export default SignupFormWithOTP;
